import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames/bind';
import React, { FunctionComponent, useMemo, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import { compose } from 'utils/compose';
import { v4 as uuid } from 'uuid';
import styles from './Input.module.scss';

interface Props extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  startAdornment?: IconProp | string;
  touched?: boolean;
  error?: boolean;
  tooltip?: string;
  classes?: {
    root?: string;
    input?: string;
  };
  inputRef?: React.LegacyRef<HTMLInputElement>;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const Input: FunctionComponent<Props> = ({
  startAdornment,
  touched,
  error,
  onBlur,
  disabled,
  classes = {},
  tooltip,
  onChange,
  inputRef,
  ...other
}) => {
  const cx = classNames.bind(styles);
  const [isFocused, setInputFocus] = useState(false);
  const tooltipUuid = useMemo(uuid, []);

  const handleFocus = () => {
    setInputFocus(true);
  };
  const handleBlur = (e: React.FocusEvent<any>) => {
    setInputFocus(false);
    if (onBlur) {
      onBlur(e);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <>
      <div
        className={cx(
          styles.root,
          {
            active: isFocused,
            error: touched && error,
            disabled,
            withInputAdornment: !!startAdornment,
          },
          classes.root
        )}
        data-tip={tooltip}
        data-for={tooltipUuid}
      >
        {!!startAdornment && (
          <div
            className={cx(styles.inputAdornment, {
              start: true,
              isText: typeof startAdornment === 'string',
            })}
          >
            {typeof startAdornment === 'string' ? (
              startAdornment
            ) : (
              <FontAwesomeIcon icon={startAdornment} className={styles.adormentIcon} />
            )}
          </div>
        )}
        <input
          {...other}
          className={cx(styles.input, classes.input)}
          disabled={disabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          ref={inputRef}
          value={other.value}
        />
      </div>
      <ReactTooltip key={tooltip} effect="solid" id={tooltipUuid} />
    </>
  );
};

export default compose<Props>(Input);
